// CRUD admin de eventos (SS-EVT 2026-05-08).
// Tabla con filtros + modal alta/edición + uploader foto + listado inscripciones.

import { useEffect, useState, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Plus, Edit2, Trash2, AlertCircle, Loader2, X, Upload, Users, Image as ImageIcon, Sparkles, ChevronUp, ChevronDown, UserPlus } from 'lucide-react';
import { createPortal } from 'react-dom';
import { leerCartelLocal } from '../../lib/leerCartelOcr';

import {
  listEventosAdmin, createEvento, updateEvento, deleteEvento,
  listProfesores, listEventoInscripciones, cancelarInscripcionEventoAdmin, subirFotoEvento, eliminarFotoEvento,
  getSugerenciaPromptIA, generarFotoIA, leerCartelEvento,
  listEventoParticipantes, createEventoParticipante, updateEventoParticipante,
  deleteEventoParticipante, reordenarParticipantes, subirFotoParticipante,
} from '../../lib/adminApi';
import { ApiError } from '../../lib/api';

const FORM_VACIO = {
  nombre_es: '', nombre_en: '', nombre_ru: '', nombre_fr: '',
  descripcion_es: '', descripcion_en: '', descripcion_ru: '', descripcion_fr: '',
  descripcion_larga_es: '', descripcion_larga_en: '', descripcion_larga_ru: '', descripcion_larga_fr: '',
  fecha: '', hora_inicio: '18:00',
  duracion_minutos: 90, lugar: '', profesor_id: '',
  precio_euros: 0, aforo_maximo: 20,
  cancelacion_dias_antes: 7, activo: true,
};

export default function AdminEventos() {
  const { t } = useTranslation();
  const [items, setItems] = useState([]);
  const [profesores, setProfesores] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editar, setEditar] = useState(null);
  const [verInscripciones, setVerInscripciones] = useState(null);

  const cargar = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const [evs, profs] = await Promise.all([listEventosAdmin(), listProfesores()]);
      setItems(evs.data || []);
      setProfesores(profs.data || []);
    } catch (e) { setError(e); } finally { setLoading(false); }
  }, []);
  useEffect(() => { cargar(); }, [cargar]);

  const onBorrar = async (item) => {
    if (!window.confirm(t('admin.eventos.confirmar_borrar', { nombre: item.nombre_es }))) return;
    try { await deleteEvento(item.id); cargar(); }
    catch (e) { alert(e.message || t('admin.error_generico')); }
  };

  const profesorNombre = (id) => profesores.find((p) => p.id === id)?.nombre_publico || '—';

  return (
    <div className="admin-page">
      <header className="admin-page-head">
        <div>
          <h1>{t('admin.eventos.titulo')}</h1>
          <p className="admin-page-sub">{t('admin.eventos.sub')}</p>
        </div>
        <button type="button" className="btn-primary" onClick={() => setEditar(FORM_VACIO)}>
          <Plus size={16} strokeWidth={1.75} /> {t('admin.crear_nuevo')}
        </button>
      </header>

      {loading && <div className="admin-loading"><Loader2 size={24} className="spin" /></div>}
      {error && <div className="admin-error"><AlertCircle size={20} /><span>{error.message}</span></div>}

      {!loading && !error && (
        <table className="admin-table">
          <thead>
            <tr>
              <th>{t('admin.eventos.col.nombre')}</th>
              <th>{t('admin.eventos.col.fecha')}</th>
              <th>{t('admin.eventos.col.profesor')}</th>
              <th>{t('admin.eventos.col.precio')}</th>
              <th>{t('admin.eventos.col.aforo')}</th>
              <th>{t('admin.eventos.col.activo')}</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {items.map((e) => (
              <tr key={e.id} className={!e.activo ? 'inactivo' : ''}>
                <td>
                  {e.imagen_url
                    ? <img src={e.imagen_url} alt="" className="admin-evento-thumb" />
                    : <span className="admin-evento-thumb admin-evento-thumb--vacio"><ImageIcon size={14} /></span>}
                  {' '}{e.nombre_es}
                </td>
                <td>{e.fecha} · {String(e.hora_inicio).slice(0, 5)}</td>
                <td>{profesorNombre(e.profesor_id)}</td>
                <td>{e.precio_euros > 0 ? `${e.precio_euros} €` : t('admin.eventos.gratis')}</td>
                <td>{e.aforo_maximo}</td>
                <td>{e.activo ? '✓' : '—'}</td>
                <td className="admin-acciones">
                  <button type="button" onClick={() => setVerInscripciones(e)} aria-label={t('admin.eventos.ver_inscripciones')} title={t('admin.eventos.ver_inscripciones')} style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                    <Users size={16} /> <span style={{ fontSize: 13 }}>{t('admin.eventos.inscripciones_btn')}</span>
                  </button>
                  <button type="button" onClick={() => setEditar({ ...FORM_VACIO, ...e, hora_inicio: String(e.hora_inicio).slice(0,5) })} aria-label={t('admin.editar')}>
                    <Edit2 size={16} />
                  </button>
                  <button type="button" onClick={() => onBorrar(e)} aria-label={t('admin.borrar')}>
                    <Trash2 size={16} />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {editar && (
        <EventoModal
          item={editar}
          profesores={profesores}
          onCerrar={() => setEditar(null)}
          onGuardado={() => { setEditar(null); cargar(); }}
        />
      )}
      {verInscripciones && (
        <InscripcionesModal
          evento={verInscripciones}
          onCerrar={() => setVerInscripciones(null)}
        />
      )}
    </div>
  );
}

function EventoModal({ item, profesores, onCerrar, onGuardado }) {
  const { t } = useTranslation();
  const [form, setForm] = useState(item);
  const [enviando, setEnviando] = useState(false);
  const [error, setError] = useState(null);
  const [errorFoto, setErrorFoto] = useState(null);
  const [subiendoFoto, setSubiendoFoto] = useState(false);
  const [imagenUrlLocal, setImagenUrlLocal] = useState(item.imagen_url || null);
  const [dialogIA, setDialogIA] = useState(false);
  const [leyendoCartel, setLeyendoCartel] = useState(false);
  const [msgCartel, setMsgCartel] = useState(null); // {ok:boolean, texto}
  const fileRef = useRef(null);
  const cartelRef = useRef(null);
  const editando = !!item.id;

  const setCampo = (k) => (e) => {
    const v = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setForm((f) => ({ ...f, [k]: v }));
  };

  const onLeerCartel = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setMsgCartel(null);
    if (!['image/jpeg', 'image/png', 'image/webp'].includes(file.type)) {
      setMsgCartel({ ok: false, texto: t('admin.eventos.foto_tipo_invalido') });
      return;
    }
    setLeyendoCartel(true);
    try {
      let d;
      try {
        d = await leerCartelLocal(file);
      } catch (_) {
        const r = await leerCartelEvento(file);
        d = r.datos || {};
      }
      setForm((f) => ({ ...f, ...d }));
      setMsgCartel({ ok: true, texto: t('admin.eventos.cartel_ok', { n: Object.keys(d).length }) });
    } catch (err) {
      const clave = err instanceof ApiError && err.code === 'OPENAI_NO_CONFIGURADO' ? 'admin.eventos.cartel_sin_clave' : 'admin.eventos.cartel_error';
      setMsgCartel({ ok: false, texto: t(clave) });
    } finally {
      setLeyendoCartel(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setError(null); setEnviando(true);
    try {
      const payload = {
        nombre_es: form.nombre_es,
        nombre_en: form.nombre_en,
        nombre_ru: form.nombre_ru || null,
        nombre_fr: form.nombre_fr || null,
        descripcion_es: form.descripcion_es,
        descripcion_en: form.descripcion_en,
        descripcion_ru: form.descripcion_ru || null,
        descripcion_fr: form.descripcion_fr || null,
        descripcion_larga_es: form.descripcion_larga_es || null,
        descripcion_larga_en: form.descripcion_larga_en || null,
        descripcion_larga_ru: form.descripcion_larga_ru || null,
        descripcion_larga_fr: form.descripcion_larga_fr || null,
        fecha: form.fecha,
        hora_inicio: form.hora_inicio,
        duracion_minutos: Number(form.duracion_minutos),
        lugar: form.lugar,
        profesor_id: form.profesor_id,
        precio_euros: Number(form.precio_euros),
        aforo_maximo: Number(form.aforo_maximo),
        cancelacion_dias_antes: Number(form.cancelacion_dias_antes),
        activo: !!form.activo,
      };
      if (editando) await updateEvento(item.id, payload);
      else await createEvento(payload);
      onGuardado();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : t('admin.error_generico'));
    } finally { setEnviando(false); }
  };

  const onSubirFoto = async (e) => {
    setErrorFoto(null);
    const file = e.target.files?.[0];
    if (!file || !item.id) return;
    if (!['image/jpeg', 'image/png', 'image/webp'].includes(file.type)) {
      setErrorFoto(t('admin.eventos.foto_tipo_invalido'));
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      setErrorFoto(t('admin.eventos.foto_demasiado_grande'));
      return;
    }
    setSubiendoFoto(true);
    try {
      const r = await subirFotoEvento(item.id, file);
      setImagenUrlLocal(r.imagen_url);
      setForm((f) => ({ ...f, imagen_url: r.imagen_url }));
    } catch (err) {
      setErrorFoto(err.message || t('admin.eventos.error_subida'));
    } finally {
      setSubiendoFoto(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const onEliminarFoto = async () => {
    if (!item.id || !imagenUrlLocal) return;
    if (!window.confirm(t('admin.eventos.foto_confirmar_eliminar'))) return;
    try {
      await eliminarFotoEvento(item.id);
      setImagenUrlLocal(null);
      setForm((f) => ({ ...f, imagen_url: null }));
    } catch (err) {
      setErrorFoto(err.message || t('admin.eventos.error_subida'));
    }
  };

  return createPortal(
    <div className="modal-backdrop" onClick={() => !enviando && onCerrar()}>
      <div className="modal modal-admin" onClick={(e) => e.stopPropagation()}>
        <button type="button" className="modal-cerrar" onClick={onCerrar}><X size={20} /></button>
        <h2 className="modal-titulo">{editando ? t('admin.editar') : t('admin.crear_nuevo')}</h2>

        <form className="modal-form" onSubmit={submit}>
          <div className="profesor-foto-section">
            <input ref={cartelRef} type="file" accept="image/jpeg,image/png,image/webp"
                   onChange={onLeerCartel} style={{ display: 'none' }} disabled={leyendoCartel} />
            <button type="button" className="btn-secondary" onClick={() => cartelRef.current?.click()} disabled={leyendoCartel}>
              {leyendoCartel
                ? <><Loader2 size={14} className="spin" /> {t('admin.eventos.cartel_leyendo')}</>
                : <><Sparkles size={14} /> {t('admin.eventos.cartel_leer')}</>}
            </button>
            <small className="modal-hint">{t('admin.eventos.cartel_hint')}</small>
            {msgCartel && (
              <div className={msgCartel.ok ? 'admin-msg-ok' : 'profesor-foto-error'}>
                {!msgCartel.ok && <AlertCircle size={14} />} {msgCartel.texto}
              </div>
            )}
          </div>
          {/* Foto: solo en modo edición (necesita id para path bucket) */}
          {editando && (
            <div className="profesor-foto-section">
              <label className="modal-campo-label">{t('admin.eventos.f.imagen')}</label>
              <div className="evento-foto-preview-wrap">
                {imagenUrlLocal
                  ? <img src={imagenUrlLocal} alt="" className="evento-foto-preview" />
                  : <div className="evento-foto-preview evento-foto-preview--vacio"><ImageIcon size={32} /></div>}
              </div>
              <div className="profesor-foto-acciones">
                <input
                  ref={fileRef}
                  type="file"
                  accept="image/jpeg,image/png,image/webp"
                  onChange={onSubirFoto}
                  style={{ display: 'none' }}
                  disabled={subiendoFoto}
                />
                <button type="button" className="btn-secondary"
                        onClick={() => fileRef.current?.click()}
                        disabled={subiendoFoto}>
                  {subiendoFoto
                    ? <><Loader2 size={14} className="spin" /> {t('admin.eventos.foto_subiendo')}</>
                    : <><Upload size={14} /> {imagenUrlLocal ? t('admin.eventos.foto_cambiar') : t('admin.eventos.foto_subir')}</>}
                </button>
                <button type="button" className="btn-secondary"
                        onClick={() => setDialogIA(true)}
                        disabled={subiendoFoto}>
                  <Sparkles size={14} /> {t('admin.eventos.foto_generar_ia')}
                </button>
                {imagenUrlLocal && (
                  <button type="button" className="btn-danger-soft"
                          onClick={onEliminarFoto} disabled={subiendoFoto}>
                    <Trash2 size={14} /> {t('admin.eventos.foto_eliminar')}
                  </button>
                )}
              </div>
              {errorFoto && (
                <div className="profesor-foto-error">
                  <AlertCircle size={14} /> {errorFoto}
                </div>
              )}
            </div>
          )}
          {!editando && (
            <p className="modal-hint">{t('admin.eventos.foto_solo_edicion')}</p>
          )}

          <div className="admin-form-grid">
            <div className="modal-campo">
              <label>{t('admin.eventos.f.nombre_es')}</label>
              <input type="text" value={form.nombre_es} onChange={setCampo('nombre_es')} required />
            </div>
            <div className="modal-campo">
              <label>{t('admin.eventos.f.nombre_en')}</label>
              <input type="text" value={form.nombre_en} onChange={setCampo('nombre_en')} required />
            </div>
            <div className="modal-campo">
              <label>{t('admin.eventos.f.fecha')}</label>
              <input type="date" value={form.fecha || ''} onChange={setCampo('fecha')} required />
            </div>
            <div className="modal-campo">
              <label>{t('admin.eventos.f.hora_inicio')}</label>
              <input type="time" value={form.hora_inicio || ''} onChange={setCampo('hora_inicio')} required />
            </div>
            <div className="modal-campo">
              <label>{t('admin.eventos.f.duracion')}</label>
              <input type="number" min="1" value={form.duracion_minutos} onChange={setCampo('duracion_minutos')} required />
            </div>
            <div className="modal-campo">
              <label>{t('admin.eventos.f.profesor')}</label>
              <select value={form.profesor_id || ''} onChange={setCampo('profesor_id')} required>
                <option value="">—</option>
                {profesores.filter((p) => p.activo !== false).map((p) => (
                  <option key={p.id} value={p.id}>{p.nombre_publico}</option>
                ))}
              </select>
            </div>
            <div className="modal-campo full">
              <label>{t('admin.eventos.f.lugar')}</label>
              <input type="text" value={form.lugar} onChange={setCampo('lugar')} required
                     placeholder={t('admin.eventos.f.lugar_placeholder')} />
            </div>
            <div className="modal-campo">
              <label>{t('admin.eventos.f.precio')}</label>
              <input type="number" step="0.01" min="0" value={form.precio_euros} onChange={setCampo('precio_euros')} required />
            </div>
            <div className="modal-campo">
              <label>{t('admin.eventos.f.aforo')}</label>
              <input type="number" min="1" value={form.aforo_maximo} onChange={setCampo('aforo_maximo')} required />
            </div>
            <div className="modal-campo">
              <label>{t('admin.eventos.f.cancelacion_dias')}</label>
              <input type="number" min="0" value={form.cancelacion_dias_antes} onChange={setCampo('cancelacion_dias_antes')} />
            </div>
            <div className="modal-campo full">
              <label>{t('admin.eventos.f.descripcion_es')}</label>
              <textarea rows={2} value={form.descripcion_es} onChange={setCampo('descripcion_es')} required />
            </div>
            <div className="modal-campo full">
              <label>{t('admin.eventos.f.descripcion_en')}</label>
              <textarea rows={2} value={form.descripcion_en} onChange={setCampo('descripcion_en')} required />
            </div>
            <div className="modal-campo full">
              <label>{t('admin.eventos.f.descripcion_larga_es')}</label>
              <textarea rows={5} value={form.descripcion_larga_es || ''} onChange={setCampo('descripcion_larga_es')}
                        placeholder={t('admin.eventos.f.descripcion_larga_placeholder')} />
            </div>
            <div className="modal-campo full">
              <label>{t('admin.eventos.f.descripcion_larga_en')}</label>
              <textarea rows={5} value={form.descripcion_larga_en || ''} onChange={setCampo('descripcion_larga_en')} />
            </div>
            <div className="modal-campo full">
              <label>
                <input type="checkbox" checked={!!form.activo} onChange={setCampo('activo')} />
                {' '}{t('admin.eventos.f.activo')}
              </label>
            </div>
          </div>

          {editando && (
            <ParticipantesSection
              eventoId={item.id}
              profesores={profesores}
              mainProfesorId={form.profesor_id}
            />
          )}

          {error && <div className="modal-error-general"><AlertCircle size={16} /><span>{error}</span></div>}

          <div className="modal-acciones">
            <button type="button" className="btn-secondary" onClick={onCerrar} disabled={enviando}>{t('comun.cancelar')}</button>
            <button type="submit" className="btn-primary" disabled={enviando}>
              {enviando ? <Loader2 size={16} className="spin" /> : t('comun.guardar')}
            </button>
          </div>
        </form>
      </div>

      {dialogIA && (
        <DialogGenerarFotoIA
          eventoId={item.id}
          onCerrar={() => setDialogIA(false)}
          onGenerado={(imagen_url) => {
            setImagenUrlLocal(imagen_url);
            setForm((f) => ({ ...f, imagen_url }));
            setDialogIA(false);
          }}
        />
      )}
    </div>,
    document.body
  );
}

// ---------------------------------------------------------------------------
// Dialog secundario: generar foto con DALL·E 3.
// ---------------------------------------------------------------------------
function DialogGenerarFotoIA({ eventoId, onCerrar, onGenerado }) {
  const { t } = useTranslation();
  const [cargando, setCargando] = useState(true);
  const [config, setConfig] = useState(null);
  const [errorCarga, setErrorCarga] = useState(null);
  const [prompt, setPrompt] = useState('');
  const [tamano, setTamano] = useState('1024x1024');
  const [calidad, setCalidad] = useState('standard');
  const [estilo, setEstilo] = useState('natural');
  const [generando, setGenerando] = useState(false);
  const [error, setError] = useState(null);
  const [confirmando, setConfirmando] = useState(false);

  // Carga inicial: prompt sugerido + opciones válidas + flag openai_configurado.
  useEffect(() => {
    let cancelled = false;
    getSugerenciaPromptIA(eventoId)
      .then((r) => {
        if (cancelled) return;
        setConfig(r);
        setPrompt(r.prompt_sugerido || '');
        setTamano(r.default?.tamano || '1024x1024');
        setCalidad(r.default?.calidad || 'standard');
        setEstilo(r.default?.estilo || 'natural');
        setCargando(false);
      })
      .catch((e) => {
        if (cancelled) return;
        setErrorCarga(e.message || t('admin.eventos.ia.error_carga'));
        setCargando(false);
      });
    return () => { cancelled = true; };
  }, [eventoId, t]);

  // Coste estimado según selección actual (cliente, tabla local).
  // Mantenemos la tabla aquí en sync con lib/openai_imagen.js (USD * 0.92).
  const costeEur = (() => {
    const PRECIOS = {
      '1024x1024': { standard: 0.04, hd: 0.07 },
      '1024x1792': { standard: 0.07, hd: 0.11 },
      '1792x1024': { standard: 0.07, hd: 0.11 },
    };
    return PRECIOS[tamano]?.[calidad] ?? null;
  })();

  const submit = async () => {
    setError(null);
    if (!prompt.trim()) {
      setError(t('admin.eventos.ia.prompt_vacio'));
      return;
    }
    if (!confirmando) {
      setConfirmando(true);
      return;
    }
    setGenerando(true);
    try {
      const r = await generarFotoIA(eventoId, { prompt, tamano, calidad, estilo });
      onGenerado(r.imagen_url);
    } catch (err) {
      const code = err instanceof ApiError ? err.code : null;
      const traduccion = t('admin.eventos.ia.errores.' + code, { defaultValue: '' });
      setError(traduccion || err.message || t('admin.eventos.ia.error_generico'));
      setGenerando(false);
      setConfirmando(false);
    }
  };

  return createPortal(
    <div className="modal-backdrop" style={{ zIndex: 1100 }} onClick={() => !generando && onCerrar()}>
      <div className="modal modal-admin" style={{ maxWidth: 580 }} onClick={(e) => e.stopPropagation()}>
        <button type="button" className="modal-cerrar" onClick={onCerrar} disabled={generando}><X size={20} /></button>
        <h2 className="modal-titulo">
          <Sparkles size={20} strokeWidth={1.5} style={{ marginRight: 6, verticalAlign: 'text-bottom' }} />
          {t('admin.eventos.ia.titulo')}
        </h2>
        <p className="auth-sub" style={{ fontSize: 14, color: 'var(--ink-muted)' }}>
          {t('admin.eventos.ia.sub')}
        </p>

        {cargando && (
          <div className="admin-loading"><Loader2 size={20} className="spin" /></div>
        )}

        {errorCarga && (
          <div className="modal-error-general">
            <AlertCircle size={16} /> <span>{errorCarga}</span>
          </div>
        )}

        {!cargando && config && !config.openai_configurado && (
          <div className="modal-error-general">
            <AlertCircle size={16} /> <span>{t('admin.eventos.ia.no_configurado')}</span>
          </div>
        )}

        {!cargando && config && config.openai_configurado && (
          <>
            <div className="modal-campo">
              <label>{t('admin.eventos.ia.prompt_label')}</label>
              <textarea
                rows={6}
                value={prompt}
                onChange={(e) => { setPrompt(e.target.value); setConfirmando(false); }}
                disabled={generando}
                maxLength={4000}
                placeholder={t('admin.eventos.ia.prompt_placeholder')}
              />
              <small style={{ color: 'var(--ink-muted)', fontSize: 11 }}>
                {prompt.length} / 4000
              </small>
            </div>

            <div className="admin-form-grid">
              <div className="modal-campo">
                <label>{t('admin.eventos.ia.tamano')}</label>
                <select value={tamano} onChange={(e) => { setTamano(e.target.value); setConfirmando(false); }} disabled={generando}>
                  <option value="1024x1024">1024 × 1024 (cuadrado)</option>
                  <option value="1792x1024">1792 × 1024 (panorámico)</option>
                  <option value="1024x1792">1024 × 1792 (vertical)</option>
                </select>
              </div>
              <div className="modal-campo">
                <label>{t('admin.eventos.ia.calidad')}</label>
                <select value={calidad} onChange={(e) => { setCalidad(e.target.value); setConfirmando(false); }} disabled={generando}>
                  <option value="standard">Standard</option>
                  <option value="hd">HD</option>
                </select>
              </div>
              <div className="modal-campo">
                <label>{t('admin.eventos.ia.estilo')}</label>
                <select value={estilo} onChange={(e) => { setEstilo(e.target.value); setConfirmando(false); }} disabled={generando}>
                  <option value="natural">{t('admin.eventos.ia.estilo_natural')}</option>
                  <option value="vivid">{t('admin.eventos.ia.estilo_vivid')}</option>
                </select>
              </div>
            </div>

            <div className="ia-coste-box">
              <span>{t('admin.eventos.ia.coste_label')}</span>
              <strong>{costeEur != null ? `~${costeEur.toFixed(2)} €` : '—'}</strong>
            </div>

            {error && (
              <div className="modal-error-general">
                <AlertCircle size={16} /> <span>{error}</span>
              </div>
            )}

            <div className="modal-acciones">
              <button type="button" className="btn-secondary" onClick={onCerrar} disabled={generando}>
                {t('comun.cancelar')}
              </button>
              <button type="button"
                      className={confirmando ? 'btn-primary' : 'btn-secondary'}
                      onClick={submit}
                      disabled={generando || !prompt.trim()}>
                {generando ? (
                  <><Loader2 size={16} className="spin" /> {t('admin.eventos.ia.generando')}</>
                ) : confirmando ? (
                  <><Sparkles size={16} /> {t('admin.eventos.ia.confirmar', { coste: costeEur != null ? costeEur.toFixed(2) : '—' })}</>
                ) : (
                  <><Sparkles size={16} /> {t('admin.eventos.ia.generar')}</>
                )}
              </button>
            </div>
            {confirmando && !generando && (
              <p style={{ fontSize: 11, color: 'var(--ink-muted)', textAlign: 'center', marginTop: 8 }}>
                {t('admin.eventos.ia.aviso_confirmacion')}
              </p>
            )}
          </>
        )}
      </div>
    </div>,
    document.body
  );
}

// ---------------------------------------------------------------------------
// SS-EVT-PARTICIPANTES — sección dentro del modal evento (solo modo edición).
// ---------------------------------------------------------------------------
function ParticipantesSection({ eventoId, profesores, mainProfesorId }) {
  const { t } = useTranslation();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dialogAdd, setDialogAdd] = useState(false);
  const [editando, setEditando] = useState(null);

  const cargar = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const r = await listEventoParticipantes(eventoId);
      setItems(r.data || []);
    } catch (e) {
      setError(e.message || t('admin.eventos.participantes.error_cargar'));
    } finally { setLoading(false); }
  }, [eventoId, t]);

  useEffect(() => { cargar(); }, [cargar]);

  const onBorrar = async (p) => {
    const nombre = p.tipo === 'profesor' ? (p.profesor?.nombre_publico || '?') : p.nombre;
    if (!window.confirm(t('admin.eventos.participantes.confirmar_borrar', { nombre }))) return;
    try { await deleteEventoParticipante(eventoId, p.id); cargar(); }
    catch (e) { alert(e.message || t('admin.error_generico')); }
  };

  const mover = async (p, delta) => {
    const idx = items.findIndex((x) => x.id === p.id);
    const newIdx = idx + delta;
    if (newIdx < 0 || newIdx >= items.length) return;
    const nuevos = [...items];
    [nuevos[idx], nuevos[newIdx]] = [nuevos[newIdx], nuevos[idx]];
    setItems(nuevos); // optimista
    try {
      const orden = nuevos.map((it, i) => ({ id: it.id, orden_visualizacion: i }));
      await reordenarParticipantes(eventoId, orden);
    } catch (e) {
      alert(e.message || t('admin.error_generico'));
      cargar(); // rollback
    }
  };

  return (
    <div className="participantes-section">
      <div className="participantes-head">
        <h3>{t('admin.eventos.participantes.titulo')}</h3>
        <button type="button" className="btn-secondary btn-mini"
                onClick={() => setDialogAdd(true)}>
          <Plus size={14} /> {t('admin.eventos.participantes.anadir')}
        </button>
      </div>
      <p className="modal-hint">{t('admin.eventos.participantes.hint')}</p>

      {loading && <div className="admin-loading"><Loader2 size={18} className="spin" /></div>}
      {error && <div className="modal-error-general"><AlertCircle size={14} /><span>{error}</span></div>}

      {!loading && !error && items.length === 0 && (
        <p className="participantes-empty">{t('admin.eventos.participantes.vacio')}</p>
      )}

      {!loading && !error && items.length > 0 && (
        <ul className="participantes-list">
          {items.map((p, idx) => {
            const fotoSrc = p.tipo === 'profesor' ? p.profesor?.foto_url : p.foto_url;
            const nombreMostrar = p.tipo === 'profesor' ? p.profesor?.nombre_publico : p.nombre;
            return (
              <li key={p.id} className="participante-row">
                <div className="participante-foto">
                  {fotoSrc
                    ? <img src={fotoSrc} alt="" />
                    : <div className="participante-foto--vacia">
                        {p.tipo === 'profesor' ? <Users size={14} /> : <UserPlus size={14} />}
                      </div>}
                </div>
                <div className="participante-info">
                  <strong>
                    {nombreMostrar || '—'}
                    <span className={`participante-pill participante-pill--${p.tipo}`}>
                      {t('admin.eventos.participantes.tipo.' + p.tipo)}
                    </span>
                  </strong>
                  {p.rol_texto && <span className="participante-rol">{p.rol_texto}</span>}
                </div>
                <div className="participante-acciones">
                  <button type="button" disabled={idx === 0} onClick={() => mover(p, -1)}
                          aria-label={t('admin.eventos.participantes.subir')}>
                    <ChevronUp size={14} />
                  </button>
                  <button type="button" disabled={idx === items.length - 1} onClick={() => mover(p, 1)}
                          aria-label={t('admin.eventos.participantes.bajar')}>
                    <ChevronDown size={14} />
                  </button>
                  <button type="button" onClick={() => setEditando(p)}
                          aria-label={t('admin.editar')}>
                    <Edit2 size={14} />
                  </button>
                  <button type="button" onClick={() => onBorrar(p)}
                          aria-label={t('admin.borrar')}>
                    <Trash2 size={14} />
                  </button>
                </div>
              </li>
            );
          })}
        </ul>
      )}

      {dialogAdd && (
        <DialogParticipante
          eventoId={eventoId}
          profesores={profesores}
          existingProfesorIds={items.filter((p) => p.tipo === 'profesor').map((p) => p.profesor_id)}
          mainProfesorId={mainProfesorId}
          onCerrar={() => setDialogAdd(false)}
          onGuardado={() => { setDialogAdd(false); cargar(); }}
        />
      )}
      {editando && (
        <DialogParticipante
          eventoId={eventoId}
          profesores={profesores}
          existingProfesorIds={items
            .filter((p) => p.tipo === 'profesor' && p.id !== editando.id)
            .map((p) => p.profesor_id)}
          mainProfesorId={mainProfesorId}
          item={editando}
          onCerrar={() => setEditando(null)}
          onGuardado={() => { setEditando(null); cargar(); }}
        />
      )}
    </div>
  );
}

function DialogParticipante({ eventoId, profesores, existingProfesorIds = [], mainProfesorId, item = null, onCerrar, onGuardado }) {
  const { t } = useTranslation();
  const editando = !!item;
  const [tipo, setTipo] = useState(item?.tipo || 'profesor');
  const [profesorId, setProfesorId] = useState(item?.profesor_id || '');
  const [nombre, setNombre] = useState(item?.nombre || '');
  const [bio, setBio] = useState(item?.bio || '');
  const [instagram, setInstagram] = useState(item?.instagram || '');
  const [webUrl, setWebUrl] = useState(item?.web_url || '');
  const [rolTexto, setRolTexto] = useState(item?.rol_texto || '');
  const [foto, setFoto] = useState(null);
  const [fotoPreview, setFotoPreview] = useState(item?.foto_url || null);
  const [enviando, setEnviando] = useState(false);
  const [error, setError] = useState(null);
  const fileRef = useRef(null);

  // Profesores disponibles: activos + no en evento ya + no es el principal del evento
  const yaUsados = new Set(existingProfesorIds);
  if (mainProfesorId) yaUsados.add(mainProfesorId);
  const profesoresDisponibles = profesores.filter((p) => {
    if (p.activo === false) return false;
    if (item?.profesor_id === p.id) return true; // si edito este mismo, dejarlo
    return !yaUsados.has(p.id);
  });

  const onElegirFoto = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!['image/jpeg', 'image/png', 'image/webp'].includes(file.type)) {
      setError(t('admin.eventos.foto_tipo_invalido'));
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      setError(t('admin.eventos.foto_demasiado_grande'));
      return;
    }
    setFoto(file);
    const reader = new FileReader();
    reader.onload = (ev) => setFotoPreview(ev.target.result);
    reader.readAsDataURL(file);
  };

  const submit = async (e) => {
    e.preventDefault();
    setError(null); setEnviando(true);
    try {
      let payload;
      if (tipo === 'profesor') {
        if (!profesorId) throw new Error(t('admin.eventos.participantes.profesor_requerido'));
        payload = {
          tipo: 'profesor',
          profesor_id: profesorId,
          rol_texto: rolTexto.trim() || null,
        };
      } else {
        if (!nombre.trim()) throw new Error(t('admin.eventos.participantes.nombre_requerido'));
        payload = {
          tipo: 'invitado',
          nombre: nombre.trim(),
          bio: bio.trim() || null,
          instagram: instagram.trim() || null,
          web_url: webUrl.trim() || null,
          rol_texto: rolTexto.trim() || null,
        };
      }
      let resultado;
      if (editando) {
        resultado = await updateEventoParticipante(eventoId, item.id, payload);
      } else {
        resultado = await createEventoParticipante(eventoId, payload);
      }
      // Foto solo invitado y solo si seleccionó archivo nuevo
      if (foto && (resultado.tipo === 'invitado' || tipo === 'invitado')) {
        await subirFotoParticipante(eventoId, resultado.id, foto);
      }
      onGuardado();
    } catch (e2) {
      setError(e2.message || t('admin.eventos.participantes.error_guardar'));
    } finally { setEnviando(false); }
  };

  return createPortal(
    <div className="modal-backdrop" style={{ zIndex: 1100 }} onClick={() => !enviando && onCerrar()}>
      <div className="modal modal-admin" style={{ maxWidth: 540 }} onClick={(e) => e.stopPropagation()}>
        <button type="button" className="modal-cerrar" onClick={onCerrar} disabled={enviando}><X size={20} /></button>
        <h2 className="modal-titulo">
          {editando ? t('admin.editar') : t('admin.eventos.participantes.anadir')}
        </h2>

        <form className="modal-form" onSubmit={submit}>
          {!editando && (
            <div className="modal-campo">
              <label>{t('admin.eventos.participantes.tipo_label')}</label>
              <div className="participante-tipo-tabs">
                <button type="button"
                        className={tipo === 'profesor' ? 'tab tab--active' : 'tab'}
                        onClick={() => { setTipo('profesor'); setError(null); }}>
                  <Users size={14} /> {t('admin.eventos.participantes.tipo.profesor')}
                </button>
                <button type="button"
                        className={tipo === 'invitado' ? 'tab tab--active' : 'tab'}
                        onClick={() => { setTipo('invitado'); setError(null); }}>
                  <UserPlus size={14} /> {t('admin.eventos.participantes.tipo.invitado')}
                </button>
              </div>
            </div>
          )}

          {tipo === 'profesor' && (
            <div className="modal-campo">
              <label>{t('admin.eventos.participantes.profesor_label')}</label>
              <select value={profesorId} onChange={(e) => setProfesorId(e.target.value)}
                      required disabled={editando}>
                <option value="">—</option>
                {profesoresDisponibles.map((p) => (
                  <option key={p.id} value={p.id}>{p.nombre_publico}</option>
                ))}
              </select>
              {editando && <small className="modal-hint">{t('admin.eventos.participantes.profesor_inmutable')}</small>}
              {!editando && profesoresDisponibles.length === 0 && (
                <small className="modal-hint">{t('admin.eventos.participantes.sin_profesores_disponibles')}</small>
              )}
            </div>
          )}

          {tipo === 'invitado' && (
            <>
              <div className="modal-campo">
                <label>{t('admin.eventos.participantes.invitado_nombre')}</label>
                <input type="text" value={nombre} onChange={(e) => setNombre(e.target.value)}
                       required maxLength={120} />
              </div>
              <div className="modal-campo full">
                <label>{t('admin.eventos.participantes.invitado_bio')}</label>
                <textarea rows={3} value={bio} onChange={(e) => setBio(e.target.value)}
                          maxLength={500}
                          placeholder={t('admin.eventos.participantes.invitado_bio_placeholder')} />
              </div>
              <div className="admin-form-grid">
                <div className="modal-campo">
                  <label>{t('admin.eventos.participantes.invitado_instagram')}</label>
                  <input type="text" value={instagram} onChange={(e) => setInstagram(e.target.value)}
                         placeholder="@usuario" maxLength={60} />
                </div>
                <div className="modal-campo">
                  <label>{t('admin.eventos.participantes.invitado_web')}</label>
                  <input type="url" value={webUrl} onChange={(e) => setWebUrl(e.target.value)}
                         placeholder="https://..." />
                </div>
              </div>
              <div className="modal-campo">
                <label>{t('admin.eventos.participantes.invitado_foto')}</label>
                <div className="participante-foto-uploader">
                  {fotoPreview && <img src={fotoPreview} alt="" className="participante-foto-preview" />}
                  <input ref={fileRef} type="file"
                         accept="image/jpeg,image/png,image/webp"
                         onChange={onElegirFoto}
                         style={{ display: 'none' }} />
                  <button type="button" className="btn-secondary btn-mini"
                          onClick={() => fileRef.current?.click()}
                          disabled={enviando}>
                    <Upload size={14} />
                    {' '}{fotoPreview
                      ? t('admin.eventos.participantes.cambiar_foto')
                      : t('admin.eventos.participantes.subir_foto')}
                  </button>
                </div>
                <small className="modal-hint">{t('admin.eventos.participantes.foto_hint')}</small>
              </div>
            </>
          )}

          <div className="modal-campo full">
            <label>{t('admin.eventos.participantes.rol_texto')}</label>
            <input type="text" value={rolTexto} onChange={(e) => setRolTexto(e.target.value)}
                   placeholder={t('admin.eventos.participantes.rol_placeholder')}
                   maxLength={200} />
          </div>

          {error && <div className="modal-error-general"><AlertCircle size={16} /><span>{error}</span></div>}

          <div className="modal-acciones">
            <button type="button" className="btn-secondary" onClick={onCerrar} disabled={enviando}>
              {t('comun.cancelar')}
            </button>
            <button type="submit" className="btn-primary"
                    disabled={enviando || (tipo === 'profesor' ? !profesorId : !nombre.trim())}>
              {enviando ? <Loader2 size={16} className="spin" /> : t('comun.guardar')}
            </button>
          </div>
        </form>
      </div>
    </div>,
    document.body
  );
}

function InscripcionesModal({ evento, onCerrar }) {
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const cargar = useCallback(() => {
    listEventoInscripciones(evento.id)
      .then((r) => setData(r.data || []))
      .catch((e) => setError(e));
  }, [evento.id]);
  useEffect(() => { cargar(); }, [cargar]);

  const cancelar = async (i) => {
    const importe = Number(i.precio_pagado_euros) || 0;
    const nombre = i.nombre || i.email || '';
    if (importe > 0) {
      if (!window.confirm(t('admin.eventos.insc.confirm_1_reembolso', { nombre, importe: importe.toFixed(2) }))) return;
      if (!window.confirm(t('admin.eventos.insc.confirm_2_reembolso', { nombre, importe: importe.toFixed(2) }))) return;
    } else if (!window.confirm(t('admin.eventos.insc.confirm_1_gratis', { nombre }))) return;
    try {
      const r = await cancelarInscripcionEventoAdmin(evento.id, i.id);
      alert(r.reembolsado ? t('admin.eventos.insc.ok_reembolsada', { importe: importe.toFixed(2) }) : t('admin.eventos.insc.ok_cancelada'));
      cargar();
    } catch (e) { alert(e.message); }
  };

  return createPortal(
    <div className="modal-backdrop" onClick={onCerrar}>
      <div className="modal modal-admin" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 720 }}>
        <button type="button" className="modal-cerrar" onClick={onCerrar}><X size={20} /></button>
        <h2 className="modal-titulo">{t('admin.eventos.inscripciones_de')}: {evento.nombre_es}</h2>
        {error && <div className="admin-error"><AlertCircle size={16} /><span>{error.message}</span></div>}
        {!error && !data && <div className="admin-loading"><Loader2 size={20} className="spin" /></div>}
        {data && data.length === 0 && <p className="eventos-empty">{t('admin.eventos.sin_inscripciones')}</p>}
        {data && data.length > 0 && (
          <table className="admin-table">
            <thead>
              <tr>
                <th>{t('admin.eventos.insc.nombre')}</th>
                <th>{t('admin.eventos.insc.email')}</th>
                <th>{t('admin.eventos.insc.estado')}</th>
                <th>{t('admin.eventos.insc.importe')}</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {data.map((i) => (
                <tr key={i.id} className={i.estado === 'cancelada' ? 'inactivo' : ''}>
                  <td>{i.nombre || '—'}</td>
                  <td>{i.email || '—'}</td>
                  <td><span className={'estado-pill estado-pill--' + i.estado}>{i.estado}</span></td>
                  <td>{Number(i.precio_pagado_euros) > 0 ? `${i.precio_pagado_euros} €` : '—'}</td>
                  <td>{i.estado !== 'cancelada' && <button type="button" className="btn-mini" onClick={() => cancelar(i)}>{t('admin.eventos.insc.cancelar')}</button>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>,
    document.body
  );
}
